import { useCallback, useContext, useMemo } from "react";
import { INCIDENT_ACTIONS } from "../../Actions/IncidentActions";
import { UserContext } from "../../Contexts/UserContext";
import { IncidentsContext } from "../../Contexts/IncidentsContext";
import { REMOVE_VEHICLE_FROM_INCIDENT } from "../../Mutations/IncidentMutations/RemoveVehicleFromIncident";
import {
  vehicleHasSwAssociation,
  vehicleHasTickets,
} from "./vehicleHasAssociations";
import { throttlePromiseFunction } from "../../utils/throttlePromiseFunction";

export const useHandleRemoveVehicle = (incident, incidentIndex, client) => {
  const { REMOVE_VIS } = INCIDENT_ACTIONS;
  const { onlineState, user } = useContext(UserContext);
  const { incidentDispatch } = useContext(IncidentsContext);

  const handleRemoveVehicle = useCallback(
    async (vis, visIndex) => {
      return new Promise((resolve, reject) => {
        (async () => {
        let errors = [];
        const vehicle = vis?.vehicle;
        if (!vis.id) {
          errors.push("VIS not found");
          resolve({ success: false, errors });
          return;
        }
        if (!incident.id && !incident?.uuid) {
          errors.push("Incident not found");
          resolve({ success: false, errors });
          return;
        }
        if (!vehicle) {
          alert("Vehicle not found");
          errors.push("Vehicle not found");
          resolve({ success: false, errors });
          return;
        }
        const { hasAssociation, associationErrors } = await vehicleHasTickets(
          vehicle,
          incident,
          user,
          client
        );
        const { hasSwAssociation, swAssociationErrors } =
          await vehicleHasSwAssociation(vehicle, incident, user, client);

        const responseErrors = associationErrors.concat(swAssociationErrors);

        if (hasAssociation || hasSwAssociation) {
          errors = errors.concat(responseErrors);
          resolve({ success: false, errors });
          return;
        }

        if (window.confirm(`Are you sure you want to remove this vehicle?`)) {
          if (onlineState) {
            try {
              client
                .mutate({
                  mutation: REMOVE_VEHICLE_FROM_INCIDENT,
                  variables: {
                    input: {
                      vehicleIncidentSpecificId: vis.id,
                      originatorId: user?.id,
                    },
                  },
                  context: { serializationKey: "incidentQueue" },
                })
                .then(mutationRes => {
                  if (
                    mutationRes?.data?.removeVehicleFromIncident?.errors
                      ?.length > 0
                  ) {
                    errors = errors.concat(
                      mutationRes?.data?.removePersonFromIncident?.errors
                    );
                    resolve({ success: false, errors });
                    return;
                  } else {
                    try {
                      incidentDispatch({
                        type: REMOVE_VIS,
                        payload: {
                          incidentId: incident?.id
                            ? incident?.id
                            : incident?.uuid,
                          incidentIndex: incidentIndex,
                          visIndex: visIndex,
                          visId: vis?.id,
                        },
                      });
                      resolve({ success: true, errors });
                      return;
                    } catch (err) {
                      errors.push(err);
                      errors.push(
                        "Could not update your local state. Please refresh the page."
                      );
                      reject({ success: false, errors });
                      return;
                    }
                  }
                })
                .catch(reject);
            } catch (err) {
              errors.push(err);
              reject({ success: false, errors });
              return;
            }
          } else {
            //offline path with state check first
            try {
              incidentDispatch({
                type: REMOVE_VIS,
                payload: {
                  incidentId: incident?.id ? incident?.id : incident?.uuid,
                  incidentIndex: incidentIndex,
                  visIndex: visIndex,
                  visId: vis?.id,
                },
              });
              client.mutate({
                mutation: REMOVE_VEHICLE_FROM_INCIDENT,
                variables: {
                  input: {
                    vehicleIncidentSpecificId: vis.id,
                    originatorId: user?.id,
                  },
                },
                context: { serializationKey: "incidentQueue" },
              }).catch(error => {
                console.error("Offline vehicle removal failed", error);
              });
              resolve({ success: true, errors });
              return;
            } catch (err) {
              errors.push(err);
              reject({ success: false, errors });
              return;
            }
          }
        } else {
          resolve({ success: true, cancelled: true, errors });
        }
        })().catch(reject);
      });
    },
    [
      incident,
      user,
      onlineState,
      client,
      incidentDispatch,
      REMOVE_VIS,
      incidentIndex,
    ]
  );
  return useMemo(
    () => throttlePromiseFunction(handleRemoveVehicle),
    [handleRemoveVehicle],
  );
};
